import React, { useState } from "react";
import { Tree } from "react-organizational-chart";
import { Button, Space } from "antd";
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  FullscreenOutlined,
} from "@ant-design/icons";
import { HierarchyUser } from "../../../utils/api/hierarchyAPI";
import EmployeeCard from "./EmployeeCard";
import TreeNode from "./TreeNode";
import "./OrganizationChart.scss";

interface OrganizationChartProps {
  data: HierarchyUser[];
  onAssignManager?: (user: HierarchyUser) => void;
  onUserClick?: (user: HierarchyUser) => void;
}

const OrganizationChart: React.FC<OrganizationChartProps> = ({
  data,
  onAssignManager,
  onUserClick,
}) => {
  const [scale, setScale] = useState(1);
  const draggedUserRef = React.useRef<HierarchyUser | null>(null);

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.1, 2));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.1, 0.5));
  };

  const handleResetZoom = () => {
    setScale(1);
  };

  const handleDragStart = (user: HierarchyUser) => {
    draggedUserRef.current = user;
  };

  const handleDragEnd = () => {
    draggedUserRef.current = null;
  };

  const handleDrop = (
    targetUser: HierarchyUser,
    droppedUser: HierarchyUser
  ) => {
    // Don't allow dropping on self
    if (targetUser._id === droppedUser._id) {
      return;
    }

    // Don't allow dropping parent on child (would create circular reference)
    const isChildOfDropped = (
      user: HierarchyUser,
      parentId: string
    ): boolean => {
      if (user._id === parentId) return true;
      if (user.children) {
        return user.children.some((child) => isChildOfDropped(child, parentId));
      }
      return false;
    };

    if (isChildOfDropped(droppedUser, targetUser._id)) {
      console.warn("Cannot create circular reference");
      return;
    }

    // Call assign manager with target as new manager
    if (onAssignManager) {
      onAssignManager(droppedUser);
    }
  };

  // Render empty state
  if (!data || data.length === 0) {
    return null;
  }

  console.log("Organization chart data:", data);

  return (
    <div className="organization-chart-wrapper">
      {/* Zoom Controls */}
      <div className="zoom-controls">
        <Space direction="vertical">
          <Button
            icon={<ZoomInOutlined />}
            onClick={handleZoomIn}
            disabled={scale >= 2}
            title="Zoom In"
          />
          <Button
            icon={<ZoomOutOutlined />}
            onClick={handleZoomOut}
            disabled={scale <= 0.5}
            title="Zoom Out"
          />
          <Button
            icon={<FullscreenOutlined />}
            onClick={handleResetZoom}
            title="Reset Zoom"
          />
        </Space>
      </div>

      {/* Organization Chart */}
      <div
        className="organization-chart-container"
        style={{ transform: `scale(${scale})`, transformOrigin: "top center" }}
      >
        {data.map((rootUser) => (
          <div key={rootUser._id} className="root-tree-wrapper">
            <Tree
              lineWidth="2px"
              lineColor="#d9d9d9"
              lineBorderRadius="10px"
              label={
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <EmployeeCard
                    user={rootUser}
                    onAssignManager={onAssignManager}
                    onClick={onUserClick}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDrop={handleDrop}
                  />
                </div>
              }
            >
              {rootUser.children &&
                rootUser.children.map((child) => (
                  <TreeNode
                    key={child._id}
                    user={child}
                    onAssignManager={onAssignManager}
                    onUserClick={onUserClick}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDrop={handleDrop}
                  />
                ))}
            </Tree>
          </div>
        ))}
      </div>

      {/* Scale Indicator */}
      <div className="scale-indicator">{Math.round(scale * 100)}%</div>
    </div>
  );
};

export default OrganizationChart;
